import * as React from "react";
import { chainCommands } from "prosemirror-commands";
import { EditorState, Transaction } from "prosemirror-state";

import {
	toggleStrong,
	strongActive,
	emActive,
	toggleEm,
	toggleU,
	uActive,
	toggleSub,
	subActive,
	toggleSup,
	supActive,
} from "./InlineCommands";
import {
	wrapInOrderedList,
	wrapInBulletedList,
	sinkListItem,
	liftListItem,
	convertToOrderedList,
	convertToBulletedList,
} from "./ListCommands";
import { indent, outdent } from "./IndentCommands";
import {
	alignLeft,
	alignCenter,
	alignRight,
	alignLeftActive,
	alignCenterActive,
	alignRightActive,
	alignJustify,
	alignJustifyActive,
} from "./AlignmentCommands";
import {
	headingActive1,
	headingActive2,
	headingActive3,
	setHeading1,
	setHeading2,
	setHeading3,
} from "./HeadingCommands";
import { insertTab } from "./TextCommands";
import { redo, undo } from "prosemirror-history";
import { clearAllFormatting } from "./ClearFormattingCommands";

export interface ButtonDefinition {
	title: string;
	icon: React.ReactNode;
	command(state: EditorState, dispatch?: (tx: Transaction) => void): boolean;
	active?(state: EditorState): boolean;
}

function fontAwesome(name: string) {
	return <span className={`fas fa-${name}`} />;
}
function headerIcon(level: number) {
	return <span>H{level}</span>;
}

export const ToolbarButtons: ButtonDefinition[][] = [
	[
		{
			title: "Bold",
			icon: fontAwesome("bold"),
			command: toggleStrong,
			active: strongActive,
		},
		{
			title: "Italic",
			icon: fontAwesome("italic"),
			command: toggleEm,
			active: emActive,
		},
		{
			title: "Underline",
			icon: fontAwesome("underline"),
			command: toggleU,
			active: uActive,
		},
	],
	[
		{
			title: "Subscript",
			icon: fontAwesome("subscript"),
			command: toggleSub,
			active: subActive,
		},
		{
			title: "Superscript",
			icon: fontAwesome("superscript"),
			command: toggleSup,
			active: supActive,
		},
	],
	[
		{
			title: "Numbered List",
			icon: fontAwesome("list-ol"),
			command: chainCommands(convertToOrderedList, wrapInOrderedList),
		},
		{
			title: "Bulleted List",
			icon: fontAwesome("list-ul"),
			command: chainCommands(convertToBulletedList, wrapInBulletedList),
		},
	],
	[
		{
			title: "Indent",
			icon: fontAwesome("indent"),
			command: chainCommands(sinkListItem, indent),
		},
		{
			title: "Outdent",
			icon: fontAwesome("outdent"),
			command: chainCommands(liftListItem, outdent),
		},
	],
	[
		{
			title: "Align Left",
			icon: fontAwesome("align-left"),
			command: alignLeft,
			active: alignLeftActive,
		},
		{
			title: "Align Center",
			icon: fontAwesome("align-center"),
			command: alignCenter,
			active: alignCenterActive,
		},
		{
			title: "Align Right",
			icon: fontAwesome("align-right"),
			command: alignRight,
			active: alignRightActive,
		},
		{
			title: "Justify",
			icon: fontAwesome("align-justify"),
			command: alignJustify,
			active: alignJustifyActive,
		},
	],
	[
		{
			title: "Heading 1",
			icon: headerIcon(1),
			command: setHeading1,
			active: headingActive1,
		},
		{
			title: "Heading 2",
			icon: headerIcon(2),
			command: setHeading2,
			active: headingActive2,
		},
		{
			title: "Heading 3",
			icon: headerIcon(3),
			command: setHeading3,
			active: headingActive3,
		},
	],
	[
		{
			title: "Undo",
			icon: fontAwesome("undo"),
			command: undo,
		},
		{
			title: "Redo",
			icon: fontAwesome("redo"),
			command: redo,
		},
	],
	[
		{
			title: "Clear Formatting",
			icon: fontAwesome("eraser"),
			command: clearAllFormatting,
		},
	],
];
